// client/src/pages/admin/project-steps/Step3Developer.tsx
import React, { useState, useEffect, useRef } from 'react';
import { StepProps } from './types';
import { getImageUrl, API_BASE } from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

interface Step3Props extends StepProps {
    developers: any[];
    setDevelopers?: React.Dispatch<React.SetStateAction<any[]>>;
    showDeveloperOnly?: boolean;
    showOverviewOnly?: boolean;
    showTowersOnly?: boolean;
}

const Step3Developer: React.FC<Step3Props> = ({ 
    form, 
    setForm, 
    handleChange, 
    developers, 
    setDevelopers,
    errors, 
    QuickUpdateButton, 
    disabledDeveloper,
    showDeveloperOnly,
    showOverviewOnly,
    showTowersOnly
}) => {
    const { token } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [coSearchTerm, setCoSearchTerm] = useState('');
    const [isCoOpen, setIsCoOpen] = useState(false);
    const [selectedCoDevId, setSelectedCoDevId] = useState('');
    const coDropdownRef = useRef<HTMLDivElement>(null);

    // New state for Developer dynamic creation
    const [devMode, setDevMode] = useState<'select' | 'create'>('select');
    const [localDevelopers, setLocalDevelopers] = useState<any[]>(developers || []);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [savingDeveloper, setSavingDeveloper] = useState(false);
    const logoInputRef = useRef<HTMLInputElement>(null);

    // New state for Co-Developer dynamic creation
    const [coDevMode, setCoDevMode] = useState<'select' | 'create'>('select');
    const [newCoDevName, setNewCoDevName] = useState('');
    const [newCoDevLogo, setNewCoDevLogo] = useState('');
    const [uploadingCoDevLogo, setUploadingCoDevLogo] = useState(false);
    const [savingCoDev, setSavingCoDev] = useState(false);
    const coDevLogoInputRef = useRef<HTMLInputElement>(null);
    const [newCoDevProjectsSince, setNewCoDevProjectsSince] = useState('');

    const updateCoDeveloper = (updatedValue: string) => {
        if (disabledDeveloper) return;
        if (setForm) {
            setForm(prev => ({
                ...prev,
                coDeveloper: updatedValue
            }));
        } else {
            handleChange({
                target: {
                    name: 'coDeveloper',
                    value: updatedValue
                }
            } as any);
        }
    };

    // Force select mode if developer details are disabled
    useEffect(() => {
        if (disabledDeveloper) {
            setDevMode('select');
            setCoDevMode('select');
        }
    }, [disabledDeveloper]);

    // Sync prop developers to local state
    useEffect(() => {
        if (developers && developers.length > 0) {
            setLocalDevelopers(developers);
        }
    }, [developers]);

    // Filter developers based on search term
    const filteredDevelopers = localDevelopers.filter(dev =>
        dev.name && dev.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Find current selected developer
    const selectedDeveloper = localDevelopers.find(dev => dev._id === form.developerId);

    // Parse coDevelopers from form.coDeveloper string
    const currentCoDevs = form.coDeveloper
        ? form.coDeveloper.split(',').map((s: string) => s.trim()).filter(Boolean)
        : [];

    // Filter co-developers (exclude primary developer and already added co-developers)
    const filteredCoDevelopers = localDevelopers.filter(dev => {
        const isPrimary = dev._id === form.developerId;
        const isAlreadyAdded = currentCoDevs.some(name => name.toLowerCase() === dev.name.toLowerCase());
        const matchesSearch = dev.name && dev.name.toLowerCase().includes(coSearchTerm.toLowerCase());
        return !isPrimary && !isAlreadyAdded && matchesSearch;
    });

    // Handle click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
            if (coDropdownRef.current && !coDropdownRef.current.contains(event.target as Node)) {
                setIsCoOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectDeveloper = (devId: string) => {
        const dev = localDevelopers.find(d => d._id === devId);
        if (dev) {
            if (setForm) {
                setForm(prev => ({
                    ...prev,
                    developerId: devId,
                    developerName: dev.name,
                    developerLogo: dev.logo || dev.avatar || '',
                    projectsSince: dev.projectsSince || '',
                    projectsCompleted: dev.projectsCompleted || '0',
                    projectsOngoing: dev.projectsOngoing || '0',
                    projectsUpcoming: dev.projectsUpcoming || '0',
                    totalProjects: dev.totalProjects || '0'
                }));
            } else {
                handleChange({ target: { name: 'developerId', value: devId } } as any);
                handleChange({ target: { name: 'developerName', value: dev.name } } as any);
                handleChange({ target: { name: 'developerLogo', value: dev.logo || dev.avatar || '' } } as any);
                handleChange({ target: { name: 'projectsSince', value: dev.projectsSince || '' } } as any);
                handleChange({ target: { name: 'projectsCompleted', value: dev.projectsCompleted || '0' } } as any);
                handleChange({ target: { name: 'projectsOngoing', value: dev.projectsOngoing || '0' } } as any);
                handleChange({ target: { name: 'projectsUpcoming', value: dev.projectsUpcoming || '0' } } as any);
            }
        }
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleAddCoDeveloper = () => {
        if (!selectedCoDevId) return;
        const dev = localDevelopers.find(d => d._id === selectedCoDevId);
        if (dev) {
            if (!currentCoDevs.some(name => name.toLowerCase() === dev.name.toLowerCase())) {
                const updatedCoDevs = [...currentCoDevs, dev.name];
                updateCoDeveloper(updatedCoDevs.join(', '));
            }
        }
        setSelectedCoDevId('');
    };

    const handleRemoveCoDeveloper = (nameToRemove: string) => {
        const updatedCoDevs = currentCoDevs.filter(name => name.toLowerCase() !== nameToRemove.toLowerCase());
        updateCoDeveloper(updatedCoDevs.join(', '));
    };

    // Handle Co-Developer logo upload
    const handleCoDevLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const uploadFormData = new FormData();
        uploadFormData.append('images', file);

        setUploadingCoDevLogo(true);
        const activeToken = token || localStorage.getItem('token');
        try {
            const res = await fetch(`${API_BASE}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${activeToken}` },
                body: uploadFormData
            });
            const data = await res.json();
            if (res.ok && data.urls && data.urls.length > 0) {
                setNewCoDevLogo(data.urls[0]);
                toast.success("Co-Developer logo uploaded successfully!");
            } else {
                toast.error(data.message || 'Logo upload failed');
            }
        } catch (err) {
            console.error('Co-Dev Logo upload error:', err);
            toast.error('Logo upload failed');
        } finally {
            setUploadingCoDevLogo(false);
        }
    };

    // Handle saving new Co-Developer profile directly
    const handleSaveAndAddCoDeveloper = async () => {
        if (!newCoDevName || !newCoDevName.trim()) {
            toast.error("Please enter a Co-Developer Name");
            return;
        }
        if (!newCoDevLogo) {
            toast.error("Please upload a Co-Developer Logo");
            return;
        }

        setSavingCoDev(true);
        const activeToken = token || localStorage.getItem('token');
        try {
            const uniqueEmail = `${newCoDevName.toLowerCase().replace(/[^a-z0-9]/g, '')}${Date.now()}@mypropertyguru.com`;
            const payload = {
                name: newCoDevName.trim(),
                email: uniqueEmail,
                password: 'password123',
                avatar: newCoDevLogo,
                projectsSince: newCoDevProjectsSince || '',
                isActive: true
            };

            const res = await fetch(`${API_BASE}/developers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (res.ok && data._id) {
                const newDev = {
                    _id: data._id,
                    name: data.name,
                    logo: data.avatar || data.logo || '',
                    projectsSince: data.projectsSince || '',
                    projectsCompleted: data.projectsCompleted || '0',
                    projectsOngoing: data.projectsOngoing || '0',
                    projectsUpcoming: data.projectsUpcoming || '0',
                    totalProjects: data.totalProjects || '0'
                };

                setLocalDevelopers(prev => [newDev, ...prev]);
                if (setDevelopers) {
                    setDevelopers(prev => [newDev, ...prev]);
                }

                const updatedCoDevs = [...currentCoDevs, newDev.name];
                updateCoDeveloper(updatedCoDevs.join(', '));

                toast.success("Co-Developer profile created and added!");
                setNewCoDevName('');
                setNewCoDevLogo('');
                setNewCoDevProjectsSince('');
                setCoDevMode('select');
            } else {
                toast.error(data.message || 'Failed to create co-developer profile');
            }
        } catch (err) {
            console.error('Save co-developer error:', err);
            toast.error('Failed to connect to server');
        } finally {
            setSavingCoDev(false);
        }
    };

    const handleUpdateCoDevYear = async (devId: string, year: string) => {
        setLocalDevelopers(prev => prev.map(d => {
            if (d._id === devId) {
                return { ...d, projectsSince: year };
            }
            return d;
        }));
        if (setDevelopers) {
            setDevelopers(prev => prev.map(d => {
                if (d._id === devId) {
                    return { ...d, projectsSince: year };
                }
                return d;
            }));
        }

        const activeToken = token || localStorage.getItem('token');
        try {
            const res = await fetch(`${API_BASE}/developers/${devId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                },
                body: JSON.stringify({ projectsSince: year })
            });
            if (res.ok) {
                toast.success("Developer established year updated successfully!");
            } else {
                const data = await res.json();
                toast.error(data.message || "Failed to update year on server");
            }
        } catch (err) {
            console.error("Error updating developer year:", err);
            toast.error("Connection error. Year updated locally only.");
        }
    };

    // Handle Developer logo upload
    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const uploadFormData = new FormData();
        uploadFormData.append('images', file);

        setUploadingLogo(true);
        const activeToken = token || localStorage.getItem('token');
        try {
            const res = await fetch(`${API_BASE}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${activeToken}` },
                body: uploadFormData
            });
            const data = await res.json();
            if (res.ok && data.urls && data.urls.length > 0) {
                if (setForm) {
                    setForm(prev => ({
                        ...prev,
                        developerLogo: data.urls[0]
                    }));
                } else {
                    handleChange({
                        target: {
                            name: 'developerLogo',
                            value: data.urls[0]
                        }
                    } as any);
                }
                toast.success("Logo uploaded successfully!");
            } else {
                toast.error(data.message || 'Logo upload failed');
            }
        } catch (err) {
            console.error('Logo upload error:', err);
            toast.error('Logo upload failed');
        } finally {
            setUploadingLogo(false);
        }
    };

    // Handle saving new Developer profile directly
    const handleSaveDeveloper = async () => {
        if (!form.developerName || !form.developerName.trim()) {
            toast.error("Please enter a Developer Name");
            return;
        }

        setSavingDeveloper(true);
        const activeToken = token || localStorage.getItem('token');
        try {
            const uniqueEmail = `${form.developerName.toLowerCase().replace(/[^a-z0-9]/g, '')}${Date.now()}@mypropertyguru.com`;
            const payload = {
                name: form.developerName.trim(),
                email: uniqueEmail,
                password: 'password123',
                avatar: form.developerLogo || '',
                projectsSince: form.projectsSince || '',
                projectsCompleted: form.projectsCompleted || '0',
                projectsOngoing: form.projectsOngoing || '0',
                projectsUpcoming: form.projectsUpcoming || '0',
                isActive: true
            };

            const res = await fetch(`${API_BASE}/developers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (res.ok && data._id) {
                const newDev = {
                    _id: data._id,
                    name: data.name,
                    logo: data.avatar || data.logo || '',
                    projectsSince: data.projectsSince,
                    projectsCompleted: data.projectsCompleted,
                    projectsOngoing: data.projectsOngoing,
                    projectsUpcoming: data.projectsUpcoming,
                    totalProjects: data.totalProjects || '0'
                };

                setLocalDevelopers(prev => [newDev, ...prev]);
                if (setDevelopers) {
                    setDevelopers(prev => [newDev, ...prev]);
                }

                if (setForm) {
                    setForm(prev => ({
                        ...prev,
                        developerId: data._id,
                        developerName: data.name,
                        developerLogo: data.avatar || '',
                        projectsSince: data.projectsSince || prev.projectsSince,
                        projectsCompleted: data.projectsCompleted || prev.projectsCompleted,
                        projectsOngoing: data.projectsOngoing || prev.projectsOngoing,
                        projectsUpcoming: data.projectsUpcoming || prev.projectsUpcoming,
                        totalProjects: data.totalProjects || prev.totalProjects
                    }));
                } else {
                    handleChange({ target: { name: 'developerId', value: data._id } } as any);
                    handleChange({ target: { name: 'developerName', value: data.name } } as any);
                    handleChange({ target: { name: 'developerLogo', value: data.avatar || '' } } as any);
                }

                toast.success("Developer profile created and selected!");
                setDevMode('select');
            } else {
                toast.error(data.message || 'Failed to create developer profile');
            }
        } catch (err) {
            console.error('Save developer error:', err);
            toast.error('Failed to connect to server');
        } finally {
            setSavingDeveloper(false);
        }
    };

    const isAll = !showDeveloperOnly && !showOverviewOnly && !showTowersOnly;
    const renderDev = showDeveloperOnly || isAll;
    const renderOverview = showOverviewOnly || isAll;
    const renderTowers = showTowersOnly || isAll;

    // Define Header Info based on active view mode
    let stepNum = "03";
    let stepTitle = "Developer Details";
    let stepSubtitle = "Information about the project developer";
    let stepIconBg = "bg-purple-600 text-white";

    if (showOverviewOnly) {
        stepNum = "04";
        stepTitle = "Project Overview";
        stepSubtitle = "General configurations and dimensions";
        stepIconBg = "bg-amber-500 text-white";
    } else if (showTowersOnly) {
        stepNum = "05";
        stepTitle = "Tower & Block Details";
        stepSubtitle = "Building structures and unit configurations";
        stepIconBg = "bg-cyan-500 text-white";
    }

    // Helper to generate years list for Established In dropdowns
    const getYearsList = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let y = currentYear; y >= 1950; y--) {
            years.push(y.toString());
        }
        return years;
    };


    return (
        <div className="space-y-10">
            {/* The previous external header is removed because the new design has the header inside the card */}

            {renderDev && (
                <>
                <div className="space-y-8 animate-in fade-in duration-500">
                    {/* Header Card */}
                    <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 text-slate-800 relative overflow-hidden shadow-sm">
                        
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg shadow-md">
                                    {parseInt(stepNum, 10)}
                                </div>
                                <div>
                                    <h3 className="text-xl font-extrabold tracking-tight uppercase text-slate-900">Developer Information</h3>
                                    <p className="text-xs text-slate-500 font-semibold mt-1">Configure primary development brand and co-venturers</p>
                                </div>
                            </div>
                            
                            {QuickUpdateButton && (
                                <div className="shrink-0">
                                    <QuickUpdateButton />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Dual Column Grid: Primary and Co-Developer */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Primary Developer Card */}
                        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm flex flex-col">
                            {/* Card Heading Header */}
                            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0F2A5F] flex items-center justify-center shadow-sm">
                                        <i className="fa-solid fa-crown text-sm"></i>
                                    </div>
                                    <span className="text-sm font-extrabold text-[#0F2A5F] uppercase tracking-wider">Primary Developer</span>
                                </div>
                                <span className="text-[10px] font-black text-rose-500 uppercase tracking-widest bg-rose-50 px-2.5 py-1 rounded-md border border-rose-100/50">Required</span>
                            </div>

                            <div className="p-6 space-y-6 flex-1">
                                {/* Mode Toggle Button Group */}
                                {!disabledDeveloper && (
                                    <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 shadow-inner w-full">
                                        <button
                                            type="button"
                                            onClick={() => setDevMode('select')}
                                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${devMode === 'select' ? 'bg-white text-[#0F2A5F] shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-800'}`}
                                        >
                                            <i className="fa-solid fa-list-ul"></i>
                                            Select Registered
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDevMode('create')}
                                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${devMode === 'create' ? 'bg-white text-[#0F2A5F] shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-800'}`}
                                        >
                                            <i className="fa-solid fa-plus"></i>
                                            Create New Profile
                                        </button>
                                    </div>
                                )}

                                {/* Developer Name Input/Select */}
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                                        Developer Name <span className="text-rose-500">*</span>
                                    </label>
                                    {devMode === 'select' ? (
                                        <div className="relative" ref={dropdownRef}>
                                            <div
                                                onClick={() => !disabledDeveloper && setIsOpen(!isOpen)}
                                                className={`w-full px-4 py-3 rounded-xl bg-white border ${errors?.developerId ? 'border-rose-500 ring-1 ring-rose-500' : 'border-slate-200'} transition-all flex items-center justify-between group ${disabledDeveloper ? 'opacity-60 cursor-not-allowed bg-slate-50' : 'hover:border-[#0F2A5F] cursor-pointer'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    {selectedDeveloper?.logo ? (
                                                        <img src={getImageUrl(selectedDeveloper.logo)} className="w-6 h-6 object-contain" alt="" />
                                                    ) : (
                                                        <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center text-slate-400">
                                                            <i className="fa-regular fa-building text-xs"></i>
                                                        </div>
                                                    )}
                                                    <span className={`text-sm font-bold ${selectedDeveloper ? 'text-slate-800' : 'text-slate-400'}`}>
                                                        {selectedDeveloper ? selectedDeveloper.name : 'Select Registered Developer'}
                                                    </span>
                                                </div>
                                                {!disabledDeveloper && (
                                                    <i className={`fa-solid fa-chevron-down text-slate-400 transition-transform ${isOpen ? 'rotate-180 text-[#0F2A5F]' : ''}`}></i>
                                                )}
                                            </div>

                                            {/* Dropdown Menu */}
                                            {isOpen && (
                                                <div className="absolute z-[100] top-full left-0 w-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                                    <div className="p-3 border-b border-slate-100 bg-slate-50/50">
                                                        <div className="relative">
                                                            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                                                            <input
                                                                type="text"
                                                                placeholder="Search developer brand..."
                                                                value={searchTerm}
                                                                onChange={e => setSearchTerm(e.target.value)}
                                                                onClick={e => e.stopPropagation()}
                                                                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] text-xs font-bold"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="max-h-60 overflow-y-auto divide-y divide-slate-50">
                                                        {filteredDevelopers.map(dev => (
                                                            <div
                                                                key={dev._id}
                                                                onClick={() => handleSelectDeveloper(dev._id)}
                                                                className="px-5 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3 transition-colors"
                                                            >
                                                                {dev.logo ? (
                                                                    <img src={getImageUrl(dev.logo)} className="w-7 h-7 object-contain bg-white p-0.5 rounded border border-slate-100" alt="" />
                                                                ) : (
                                                                    <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center text-slate-400 text-xs">
                                                                        {dev.name.charAt(0)}
                                                                    </div>
                                                                )}
                                                                <div className="flex-1">
                                                                    <p className="text-xs font-bold text-slate-800">{dev.name}</p>
                                                                    {dev.projectsSince && <p className="text-[10px] text-slate-400 font-medium">Est. {dev.projectsSince}</p>}
                                                                </div>
                                                                {form.developerId === dev._id && (
                                                                    <i className="fa-solid fa-circle-check text-emerald-500"></i>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {filteredDevelopers.length === 0 && (
                                                            <div className="px-5 py-6 text-center text-xs font-extrabold text-slate-400 uppercase tracking-wider">
                                                                No Developers Found
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <input
                                                type="text"
                                                name="developerName"
                                                value={form.developerName || ''}
                                                onChange={handleChange}
                                                placeholder="Enter Developer Brand Name"
                                                className={`w-full px-4 py-3 rounded-xl bg-white border ${errors?.developerId && !form.developerName ? 'border-rose-500 focus:border-rose-500' : 'border-slate-200 focus:border-[#0F2A5F]'} outline-none transition-all text-sm font-bold text-slate-800`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setDevMode('select');
                                                    if(setForm) setForm(prev => ({ ...prev, developerName: '' }));
                                                }}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-[#0F2A5F] hover:underline"
                                            >
                                                Select Existing
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Established In */}
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                                        Established In
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                                            <i className="fa-regular fa-calendar-days"></i>
                                        </div>
                                        <select
                                            disabled={disabledDeveloper}
                                            name="projectsSince"
                                            value={form.projectsSince || ''}
                                            onChange={handleChange}
                                            className={`w-full pl-11 pr-10 py-3 rounded-xl bg-white border border-slate-200 outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] transition-all text-sm font-bold text-slate-800 appearance-none ${disabledDeveloper ? 'bg-slate-50 opacity-60 cursor-not-allowed' : ''}`}
                                        >
                                            <option value="">Select Established Year</option>
                                            {getYearsList().map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-400">
                                            <i className="fa-solid fa-chevron-down text-xs"></i>
                                        </div>
                                    </div>
                                </div>

                                {/* Developer Logo */}
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                                        Developer Logo <span className="text-rose-500">*</span>
                                    </label>
                                    
                                    {devMode === 'create' ? (
                                        <div 
                                            onClick={() => !uploadingLogo && logoInputRef.current?.click()}
                                            className={`border-2 border-dashed border-slate-300 hover:border-[#0F2A5F] rounded-2xl p-6 flex flex-col items-center justify-center text-center bg-slate-50 hover:bg-blue-50/10 transition-all cursor-pointer relative min-h-[148px] ${uploadingLogo ? 'pointer-events-none' : ''}`}
                                        >
                                            <input 
                                                type="file" 
                                                ref={logoInputRef}
                                                className="hidden" 
                                                onChange={handleLogoUpload} 
                                                accept="image/*" 
                                            />
                                            {uploadingLogo ? (
                                                <div className="flex flex-col items-center justify-center">
                                                    <div className="w-8 h-8 border-3 border-[#0F2A5F] border-t-transparent rounded-full animate-spin mb-2"></div>
                                                    <p className="text-xs font-bold text-[#0F2A5F]">Uploading Logo...</p>
                                                </div>
                                            ) : form.developerLogo ? (
                                                <div className="flex flex-col items-center justify-center">
                                                    <img src={getImageUrl(form.developerLogo)} className="max-h-16 max-w-full object-contain mb-2 p-1 bg-white rounded border border-slate-100 shadow-sm animate-in fade-in" alt="Uploaded Logo" />
                                                    <p className="text-xs font-bold text-emerald-600 flex items-center gap-1"><i className="fa-solid fa-circle-check"></i> Logo Uploaded</p>
                                                    <p className="text-[10px] text-slate-400 font-medium mt-1">Click to replace</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-2">
                                                        <i className="fa-regular fa-image text-lg"></i>
                                                    </div>
                                                    <p className="text-xs font-bold text-slate-700">Click to Upload Brand Logo</p>
                                                    <p className="text-[10px] text-slate-400 font-medium mt-1">PNG, JPG or SVG (Max 2MB)</p>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="border border-slate-150 rounded-2xl p-6 flex flex-col items-center justify-center text-center bg-slate-50/50 min-h-[148px] shadow-inner">
                                            {form.developerLogo ? (
                                                <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-center max-w-[180px] animate-in fade-in">
                                                    <img src={getImageUrl(form.developerLogo)} className="max-h-16 object-contain" alt="Developer Logo" />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-300 flex items-center justify-center mb-2">
                                                        <i className="fa-regular fa-building text-lg"></i>
                                                    </div>
                                                    <p className="text-xs font-bold text-slate-400">Select a developer to load logo</p>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Save New Developer Button (Only in create mode) */}
                            {devMode === 'create' && (
                                <div className="p-6 border-t border-slate-100 bg-slate-50/50">
                                    <button
                                        type="button"
                                        onClick={handleSaveDeveloper}
                                        disabled={savingDeveloper || uploadingLogo}
                                        className="w-full py-3 bg-[#0F2A5F] hover:bg-black text-white rounded-xl font-bold text-xs shadow-md transition-all active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        {savingDeveloper ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Creating Developer Profile...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-floppy-disk"></i>
                                                <span>Save & Select Brand</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Co-Developer Card */}
                        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm flex flex-col">
                            {/* Card Heading Header */}
                            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-lg bg-amber-50 text-[#D4A017] flex items-center justify-center shadow-sm">
                                        <i className="fa-solid fa-handshake text-sm"></i>
                                    </div>
                                    <span className="text-sm font-extrabold text-[#0F2A5F] uppercase tracking-wider">Co-Developer</span>
                                </div>
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-2.5 py-1 rounded-md border border-slate-200/50">Optional</span>
                            </div>

                            <div className="p-6 space-y-6 flex-1">
                                {/* Co-Dev Mode Selector Toggle */}
                                {!disabledDeveloper && (
                                    <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 shadow-inner w-full">
                                        <button
                                            type="button"
                                            onClick={() => setCoDevMode('select')}
                                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${coDevMode === 'select' ? 'bg-white text-[#0F2A5F] shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-800'}`}
                                        >
                                            <i className="fa-solid fa-list-ul"></i>
                                            Select Registered
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCoDevMode('create')}
                                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${coDevMode === 'create' ? 'bg-white text-[#0F2A5F] shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-800'}`}
                                        >
                                            <i className="fa-solid fa-plus"></i>
                                            Create & Add Brand
                                        </button>
                                    </div>
                                )}

                                {/* Co-Developer Selector Input */}
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                                        Co-Developer(s)
                                    </label>
                                    
                                    {disabledDeveloper ? (
                                        <div className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm font-bold text-slate-500 cursor-not-allowed">
                                            {form.coDeveloper || 'No Co-Developer Configured'}
                                        </div>
                                    ) : coDevMode === 'select' ? (
                                        <div className="relative" ref={coDropdownRef}>
                                            <div
                                                onClick={() => setIsCoOpen(!isCoOpen)}
                                                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 hover:border-[#0F2A5F] transition-all cursor-pointer flex items-center justify-between group"
                                            >
                                                <span className="text-sm font-bold text-slate-400">
                                                    Select Co-Developer Brand
                                                </span>
                                                <i className={`fa-solid fa-chevron-down text-slate-400 transition-transform ${isCoOpen ? 'rotate-180 text-[#0F2A5F]' : ''}`}></i>
                                            </div>
                                            
                                            {/* Co-Developer Dropdown Menu */}
                                            {isCoOpen && (
                                                <div className="absolute z-[100] top-full left-0 w-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                                    <div className="p-3 border-b border-slate-100 bg-slate-50/50">
                                                        <div className="relative">
                                                            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                                                            <input
                                                                type="text"
                                                                placeholder="Search co-developer brand..."
                                                                value={coSearchTerm}
                                                                onChange={e => setCoSearchTerm(e.target.value)}
                                                                onClick={e => e.stopPropagation()}
                                                                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] text-xs font-bold"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="max-h-60 overflow-y-auto divide-y divide-slate-50">
                                                        {filteredCoDevelopers.map(dev => (
                                                            <div
                                                                key={dev._id}
                                                                onClick={() => {
                                                                    const devName = dev.name;
                                                                    if (!currentCoDevs.some(name => name.toLowerCase() === devName.toLowerCase())) {
                                                                        const updatedCoDevs = [...currentCoDevs, devName];
                                                                        updateCoDeveloper(updatedCoDevs.join(', '));
                                                                    }
                                                                    setIsCoOpen(false);
                                                                    setCoSearchTerm('');
                                                                }}
                                                                className="px-5 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3 transition-colors"
                                                            >
                                                                {dev.logo ? (
                                                                    <img src={getImageUrl(dev.logo)} className="w-7 h-7 object-contain bg-white p-0.5 rounded border border-slate-100" alt="" />
                                                                ) : (
                                                                    <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center text-slate-400 text-xs">
                                                                        {dev.name.charAt(0)}
                                                                    </div>
                                                                )}
                                                                <div className="flex-1">
                                                                    <p className="text-xs font-bold text-slate-800">{dev.name}</p>
                                                                    {dev.projectsSince && <p className="text-[10px] text-slate-400 font-medium">Est. {dev.projectsSince}</p>}
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {filteredCoDevelopers.length === 0 && (
                                                            <div className="px-5 py-4 text-center text-xs font-extrabold text-slate-400 uppercase tracking-wider">
                                                                No Developers Available
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="space-y-4 p-4 border border-slate-200 rounded-2xl bg-slate-50/30">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">Co-Developer Name <span className="text-rose-500">*</span></label>
                                                <input
                                                    type="text"
                                                    value={newCoDevName}
                                                    onChange={e => setNewCoDevName(e.target.value)}
                                                    placeholder="Enter Co-Developer Name"
                                                    className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-[#0F2A5F] outline-none text-xs font-bold text-slate-800"
                                                />
                                            </div>
                                            
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">Established In</label>
                                                <div className="relative">
                                                    <select
                                                        value={newCoDevProjectsSince}
                                                        onChange={e => setNewCoDevProjectsSince(e.target.value)}
                                                        className="w-full pl-3 pr-10 py-2.5 rounded-xl bg-white border border-slate-200 outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] text-xs font-bold text-slate-800 appearance-none"
                                                    >
                                                        <option value="">Select Established Year</option>
                                                        {getYearsList().map(year => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-slate-400">
                                                        <i className="fa-solid fa-chevron-down text-[10px]"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <label className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">Brand Logo <span className="text-rose-500">*</span></label>
                                                <div 
                                                    onClick={() => !uploadingCoDevLogo && coDevLogoInputRef.current?.click()}
                                                    className={`border border-dashed border-slate-300 hover:border-[#0F2A5F] rounded-xl p-3 flex flex-col items-center justify-center text-center bg-white hover:bg-blue-50/10 transition-all cursor-pointer relative min-h-[90px] ${uploadingCoDevLogo ? 'pointer-events-none' : ''}`}
                                                >
                                                    <input 
                                                        type="file" 
                                                        ref={coDevLogoInputRef}
                                                        className="hidden" 
                                                        onChange={handleCoDevLogoUpload} 
                                                        accept="image/*" 
                                                    />
                                                    {uploadingCoDevLogo ? (
                                                        <div className="flex flex-col items-center justify-center">
                                                            <div className="w-5 h-5 border-2 border-[#0F2A5F] border-t-transparent rounded-full animate-spin mb-1"></div>
                                                            <p className="text-[9px] font-bold text-[#0F2A5F]">Uploading...</p>
                                                        </div>
                                                    ) : newCoDevLogo ? (
                                                        <div className="flex flex-col items-center justify-center">
                                                            <img src={getImageUrl(newCoDevLogo)} className="max-h-10 object-contain mb-1" alt="Co-Developer Logo Preview" />
                                                            <p className="text-[9px] font-bold text-emerald-600"><i className="fa-solid fa-circle-check"></i> Uploaded</p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <i className="fa-regular fa-image text-lg text-slate-400 mb-0.5"></i>
                                                            <p className="text-[10px] font-bold text-slate-700">Click to Upload Logo</p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={handleSaveAndAddCoDeveloper}
                                                disabled={savingCoDev || uploadingCoDevLogo}
                                                className="w-full py-2 bg-[#0F2A5F] hover:bg-black text-white rounded-xl font-bold text-xs shadow-md transition-all active:scale-95 flex items-center justify-center gap-1.5"
                                            >
                                                {savingCoDev ? (
                                                    <>
                                                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                        <span>Adding Brand...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fa-solid fa-plus text-[10px]"></i>
                                                        <span>Add Co-Developer</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Established Years of selected co-developers */}
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                                        Established Details
                                    </label>
                                    <div className="bg-slate-50/50 border border-slate-150 rounded-2xl p-4 min-h-[50px] flex flex-col justify-center">
                                        {currentCoDevs.length > 0 ? (
                                            <div className="space-y-1.5">
                                                {currentCoDevs.map((name, idx) => {
                                                    const dev = localDevelopers.find(d => d.name.toLowerCase() === name.toLowerCase());
                                                    return (
                                                        <div key={idx} className="flex justify-between items-center text-xs font-bold text-slate-700 gap-4">
                                                            <span className="text-slate-800">{name}</span>
                                                            <div className="flex items-center gap-2">
                                                                {dev ? (
                                                                    <select
                                                                        disabled={disabledDeveloper}
                                                                        value={dev.projectsSince || ''}
                                                                        onChange={(e) => handleUpdateCoDevYear(dev._id, e.target.value)}
                                                                        className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-bold text-slate-700 outline-none focus:border-[#0F2A5F] focus:ring-1 focus:ring-[#0F2A5F] cursor-pointer"
                                                                    >
                                                                        <option value="">Select Established Year</option>
                                                                        {getYearsList().map(year => (
                                                                            <option key={year} value={year}>{year}</option>
                                                                        ))}
                                                                    </select>
                                                                ) : null}
                                                                {!disabledDeveloper && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveCoDeveloper(name)}
                                                                        className="w-5 h-5 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all active:scale-95"
                                                                        title="Remove co-developer"
                                                                    >
                                                                        <i className="fa-solid fa-trash-can text-[10px]"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-slate-400 font-bold text-center">No co-developers selected</p>
                                        )}
                                    </div>
                                </div>

                                {/* Selected Co-Developer Chips and Logos Grid */}
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                                        Selected Co-Developer Logos
                                    </label>
                                    
                                    <div className="border border-slate-150 rounded-2xl p-4 flex flex-wrap gap-4 items-center justify-center bg-slate-50/30 min-h-[120px] shadow-inner">
                                        {currentCoDevs.length > 0 ? (
                                            currentCoDevs.map((name, idx) => {
                                                const dev = localDevelopers.find(d => d.name.toLowerCase() === name.toLowerCase());
                                                return (
                                                    <div key={idx} className="relative flex flex-col items-center bg-white p-2.5 rounded-xl border border-slate-200 shadow-sm max-w-[100px] text-center group">
                                                        {!disabledDeveloper && (
                                                            <button 
                                                                type="button" 
                                                                onClick={() => handleRemoveCoDeveloper(name)}
                                                                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-[9px]"
                                                                title="Remove co-developer"
                                                            >
                                                                <i className="fa-solid fa-xmark"></i>
                                                            </button>
                                                        )}
                                                        {dev?.logo ? (
                                                            <img src={getImageUrl(dev.logo)} className="h-10 object-contain animate-in fade-in" alt={name} />
                                                        ) : (
                                                            <div className="h-10 w-10 bg-gradient-to-br from-[#0F2A5F] to-slate-800 rounded-lg flex items-center justify-center text-white text-xs font-black shadow-inner">
                                                                {name.charAt(0)}
                                                            </div>
                                                        )}
                                                        <span className="text-[9px] font-black text-slate-600 max-w-[80px] truncate mt-1">{name}</span>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center text-slate-400">
                                                <i className="fa-regular fa-building text-2xl mb-1 block"></i>
                                                <p className="text-[10px] font-extrabold uppercase tracking-wider">No Co-Developer Logos</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> {/* End Grid */}

                    {/* Developer Project Statistics - KPI Dashboard Card Row */}
                    <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                        <h4 className="text-sm font-extrabold text-[#0F2A5F] uppercase tracking-wider mb-6 flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#0F2A5F] flex items-center justify-center shadow-sm">
                                <i className="fa-solid fa-chart-line text-xs"></i>
                            </div>
                            Developer Projects statistics
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Completed projects stats card */}
                            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex items-center gap-4 relative overflow-hidden transition-all hover:shadow-md">
                                <div className="absolute right-0 bottom-0 text-emerald-500/5 -translate-y-2 translate-x-2 text-7xl font-black select-none pointer-events-none">
                                    <i className="fa-solid fa-building-circle-check"></i>
                                </div>
                                <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-500 shrink-0 shadow-sm">
                                    <i className="fa-solid fa-building-circle-check text-2xl"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-[10px] font-black uppercase text-slate-400 tracking-wider">Completed Projects</span>
                                    <div className="flex items-baseline gap-2 mt-1">
                                        <input 
                                            type="text" 
                                            name="projectsCompleted" 
                                            disabled={disabledDeveloper} 
                                            value={form.projectsCompleted || ''} 
                                            onChange={handleChange} 
                                            className={`w-20 text-3xl font-black text-slate-800 leading-none outline-none bg-transparent border-b border-transparent ${!disabledDeveloper ? 'hover:border-slate-300 focus:border-[#0F2A5F] focus:bg-white px-1' : 'cursor-not-allowed opacity-70'}`} 
                                            placeholder="0" 
                                        />
                                        <span className="text-xs font-bold text-slate-400 uppercase">Projects</span>
                                    </div>
                                </div>
                            </div>

                            {/* Ongoing projects stats card */}
                            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex items-center gap-4 relative overflow-hidden transition-all hover:shadow-md">
                                <div className="absolute right-0 bottom-0 text-[#0F2A5F]/5 -translate-y-2 translate-x-2 text-7xl font-black select-none pointer-events-none">
                                    <i className="fa-solid fa-crane"></i>
                                </div>
                                <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-[#0F2A5F] shrink-0 shadow-sm">
                                    <i className="fa-solid fa-crane text-2xl"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-[10px] font-black uppercase text-slate-400 tracking-wider">Ongoing Projects</span>
                                    <div className="flex items-baseline gap-2 mt-1">
                                        <input 
                                            type="text" 
                                            name="projectsOngoing" 
                                            disabled={disabledDeveloper} 
                                            value={form.projectsOngoing || ''} 
                                            onChange={handleChange} 
                                            className={`w-20 text-3xl font-black text-slate-800 leading-none outline-none bg-transparent border-b border-transparent ${!disabledDeveloper ? 'hover:border-slate-300 focus:border-[#0F2A5F] focus:bg-white px-1' : 'cursor-not-allowed opacity-70'}`} 
                                            placeholder="0" 
                                        />
                                        <span className="text-xs font-bold text-slate-400 uppercase">Projects</span>
                                    </div>
                                </div>
                            </div>

                            {/* Upcoming projects stats card */}
                            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex items-center gap-4 relative overflow-hidden transition-all hover:shadow-md">
                                <div className="absolute right-0 bottom-0 text-[#D4A017]/5 -translate-y-2 translate-x-2 text-7xl font-black select-none pointer-events-none">
                                    <i className="fa-regular fa-calendar-plus"></i>
                                </div>
                                <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-150 flex items-center justify-center text-[#D4A017] shrink-0 shadow-sm">
                                    <i className="fa-regular fa-calendar-plus text-2xl"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-[10px] font-black uppercase text-slate-400 tracking-wider">Upcoming Projects</span>
                                    <div className="flex items-baseline gap-2 mt-1">
                                        <input 
                                            type="text" 
                                            name="projectsUpcoming" 
                                            disabled={disabledDeveloper} 
                                            value={form.projectsUpcoming || ''} 
                                            onChange={handleChange} 
                                            className={`w-20 text-3xl font-black text-slate-800 leading-none outline-none bg-transparent border-b border-transparent ${!disabledDeveloper ? 'hover:border-slate-300 focus:border-[#0F2A5F] focus:bg-white px-1' : 'cursor-not-allowed opacity-70'}`} 
                                            placeholder="0" 
                                        />
                                        <span className="text-xs font-bold text-slate-400 uppercase">Projects</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </>
            )}

            {renderOverview && (
                <section className="bg-white p-8 lg:p-10 border border-gray-100 rounded-[2.5rem] space-y-10 relative overflow-hidden shadow-sm">
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-50 rounded-tr-full -z-10 rotate-90"></div>

                    <div className="flex items-center gap-4 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-500 border border-amber-100 shadow-inner">
                            <i className="fa-solid fa-list-check text-lg"></i>
                        </div>
                        <h6 className="text-lg font-bold text-gray-800">Project Overview</h6>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {([
                            { label: 'Configuration', key: 'overviewConfiguration', placeholder: '2, 3 BHK' },
                            { label: 'Total Amenities', key: 'overviewTotalPlotArea', placeholder: 'e.g. 30+ Premium Amenities' },
                            { label: 'Total Plot Area *', key: 'overviewProjectArea', placeholder: '5.5 ACRES' },
                            { label: 'Avg Price/SQ.FT', key: 'overviewAvgPricePerSqFt', placeholder: '₹11,290 PER SQ.FT' },
                            { label: 'Open Space %', key: 'overviewOpenSpacePercentage', placeholder: '65%' },
                            { label: 'Bathrooms', key: 'overviewBathrooms', placeholder: 'e.g. 2' },
                            { label: 'Age of Construction', key: 'overviewAgeOfConstruction', placeholder: 'New Construction' },
                            { label: 'Parking', key: 'overviewParking', placeholder: '1 Covered' }
                        ] as any[]).map((field, i) => (
                            <div key={i} className="space-y-3">
                                <label className="text-xs font-bold text-gray-500 ml-2">{field.label} *</label>
                                {field.type === 'select' ? (
                                    <select
                                        className={`w-full px-6 py-4 rounded-2xl bg-gray-50 border ${errors?.[field.key] ? 'border-rose-500 bg-rose-55 text-rose-500' : 'border-gray-200 text-gray-800'} outline-none focus:border-purple-500 transition-all font-bold shadow-inner appearance-none uppercase`}
                                        name={field.key}
                                        value={(form as any)[field.key] || ''}
                                        onChange={handleChange}
                                    >
                                        <option value="">-- SELECT --</option>
                                        {field.options?.map((opt: string) => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input type="text" className={`w-full px-6 py-4 rounded-2xl bg-gray-50 border ${errors?.[field.key] ? 'border-rose-500 bg-rose-50 text-rose-500' : 'border-gray-200 text-gray-800'} outline-none focus:border-purple-500 transition-all font-bold shadow-inner`} name={field.key} value={(form as any)[field.key] || ''} onChange={handleChange} placeholder={field.placeholder} />
                                )}
                            </div>
                        ))}
                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 ml-2">Completion Progress *</label>
                            <div className="flex items-center gap-3 relative">
                                <input type="number" min="0" max="100" className={`w-full px-6 py-4 rounded-2xl bg-purple-50 border ${errors?.progressPercentage ? 'border-rose-500 bg-rose-50 text-rose-500' : 'border-gray-200 text-gray-800'} outline-none focus:border-purple-500 transition-all font-bold text-xl shadow-inner pr-12`} name="progressPercentage" value={form.progressPercentage} onChange={handleChange} />
                                <span className="absolute right-6 font-bold text-purple-600 text-xl">%</span>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {renderTowers && (
                <section className="bg-white p-8 lg:p-10 border border-gray-150 rounded-[2.5rem] space-y-10 relative overflow-hidden shadow-sm">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-500 border border-cyan-100 shadow-inner">
                            <i className="fa-solid fa-layer-group text-lg"></i>
                        </div>
                        <h6 className="text-lg font-bold text-gray-800">Tower & Block Details</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[
                            { label: 'Towers / Blocks', key: 'overviewTotalTowers', placeholder: '4' },
                            { label: 'Total Floors', key: 'overviewTotalFloors', placeholder: '24' },
                            { label: 'Sub-G + Floors', key: 'overviewFloors', placeholder: '2B + G + 22' },
                            { label: 'Total Units', key: 'overviewTotalUnits', placeholder: '450' }
                        ].map((field, i) => (
                            <div key={i} className="space-y-3">
                                <label className="text-xs font-bold text-gray-500 ml-2">{field.label} *</label>
                                <input type="text" className={`w-full px-6 py-4 bg-gray-50 border ${errors?.[field.key] ? 'border-rose-500 bg-rose-50 text-rose-500' : 'border-gray-200 text-gray-800'} rounded-2xl outline-none font-bold text-base shadow-inner`} name={field.key} value={(form as any)[field.key] || ''} onChange={handleChange} placeholder={field.placeholder} />
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default Step3Developer;
